import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await api.me();
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Resolves to the signed-in user, or to { mfaRequired: true } when the
   * account has two-step verification on - the caller then collects a
   * code and calls completeTotp.
   */
  const login = useCallback(async (userId, password) => {
    const res = await api.login(userId, password);
    if (res.mfaRequired) return { mfaRequired: true };
    setUser(res.user);
    return res.user;
  }, []);

  /** Second step of sign-in: a code from the app, or a recovery code. */
  const completeTotp = useCallback(async (body) => {
    const { user: me } = await api.totpVerify(body);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, completeTotp, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Where each role lands after signing in. */
export const homeFor = (role) =>
  ({ student: '/learn', admin: '/admin', superadmin: '/manage' })[role] ?? '/login';
